import { useMatch, useNavigate, useParams } from "@tanstack/react-router";
import { useServiceIdOverride } from "contexts/serviceIdOverride";
import { useCallback, useMemo } from "react";
import type { ExplorerHomeTab, ExplorerObjectTab } from "routes/searchParams";

/**
 * The Explorer is rendered in two areas: under a service, and under a branch.
 *
 * The pages, the tabs and the data hooks are the same in both. Only the URL
 * differs: the branch area keeps the branch segment and adds a `branchId`
 * param, and on a branch route `serviceId` is the parent service.
 *
 * The two path sets live in `SERVICE_EXPLORER_PATHS` and
 * `BRANCH_EXPLORER_PATHS`, and `explorerTarget` is the one place that picks
 * between them. Every navigation, redirect and link asks for a target rather
 * than writing its path, so a target is declared once per area here and its
 * route once in `router.tsx`.
 *
 * The branch routes have no legacy redirects (`/storage`, `/columnstore`,
 * `/data-tiering`, `/cagg`): those exist for saved service links.
 */

/**
 * The Explorer's paths, per area. The pages and the route ids are derived from
 * the layout, so a page is added in one place. Exported so the redirect and the
 * pages that build a link do not restate them.
 */
const SERVICE_EXPLORER =
  "/projects/{$projectId}/services/{$serviceId}/explorer" as const;
const BRANCH_EXPLORER =
  "/projects/{$projectId}/services/{$serviceId}/branches/{$branchId}/explorer" as const;

export const SERVICE_EXPLORER_PATHS = {
  layout: SERVICE_EXPLORER,
  home: `${SERVICE_EXPLORER}/general`,
  object: `${SERVICE_EXPLORER}/object/{$schema}/{$objectType}/{$objectName}`,
  query: `${SERVICE_EXPLORER}/query/{$queryId}`,
} as const;

export const BRANCH_EXPLORER_PATHS = {
  layout: BRANCH_EXPLORER,
  home: `${BRANCH_EXPLORER}/general`,
  object: `${BRANCH_EXPLORER}/object/{$schema}/{$objectType}/{$objectName}`,
  query: `${BRANCH_EXPLORER}/query/{$queryId}`,
} as const;

/**
 * The route ids of the targets, per area, for `useMatch`: the area's paths
 * under `authLayout`.
 */
const SERVICE_MATCHES = {
  home: `/authLayout${SERVICE_EXPLORER_PATHS.home}` as const,
  object: `/authLayout${SERVICE_EXPLORER_PATHS.object}` as const,
  query: `/authLayout${SERVICE_EXPLORER_PATHS.query}` as const,
} as const;

const BRANCH_MATCHES = {
  home: `/authLayout${BRANCH_EXPLORER_PATHS.home}` as const,
  object: `/authLayout${BRANCH_EXPLORER_PATHS.object}` as const,
  query: `/authLayout${BRANCH_EXPLORER_PATHS.query}` as const,
} as const;

/**
 * An Explorer area, and the route params that address it. On a branch route
 * `serviceId` is the parent service and `branchId` is the branch the Explorer
 * reads; on a service route `branchId` is empty and unused.
 */
export type ExplorerArea = {
  isBranch: boolean;
  projectId: string;
  serviceId: string;
  branchId: string;
};

/** The pages of the Explorer, and the params each one takes from its URL. */
export type ExplorerTargetParams = {
  home: Record<never, never>;
  object: { schema: string; objectType: string; objectName: string };
  query: { queryId: string };
};

/** A page of the Explorer, by name. */
export type ExplorerTarget = keyof ExplorerTargetParams;

/**
 * Whether the Explorer is the branch one.
 *
 * The branch Explorer layout provides the service id override, and nothing else
 * does, so its presence is the single rule: the links, the data hooks and the
 * SQL engine all use it, instead of each deciding from the route.
 */
export const useIsBranchExplorer = (): boolean =>
  useServiceIdOverride() !== undefined;

/**
 * The area's route params, and which area it is. Memoized so the callbacks and
 * the hooks that read the area can put it in a dependency list.
 */
export const useExplorerArea = (): ExplorerArea => {
  const { projectId, serviceId, branchId } = useParams({ strict: false });
  const isBranch = useIsBranchExplorer();
  return useMemo(
    () => ({
      isBranch,
      projectId: projectId ?? "",
      serviceId: serviceId ?? "",
      branchId: branchId ?? "",
    }),
    [isBranch, projectId, serviceId, branchId],
  );
};

/**
 * Where a target lives in an area, as the `to`/`params` pair the router takes.
 * This is the one place the two path sets are chosen between: `navigate`,
 * `redirect`, `<Navigate>` and the link builders all ask for a target.
 */
export const explorerTarget = <TTarget extends ExplorerTarget>(
  area: ExplorerArea,
  target: TTarget,
  params: ExplorerTargetParams[TTarget],
) => {
  const paths = area.isBranch ? BRANCH_EXPLORER_PATHS : SERVICE_EXPLORER_PATHS;
  return {
    to: paths[target],
    params: {
      projectId: area.projectId,
      serviceId: area.serviceId,
      ...(area.isBranch ? { branchId: area.branchId } : {}),
      ...params,
    },
  };
};

/**
 * The path of an area's Explorer root, the parent of its pages. Used to tell
 * whether a route is already in the Explorer.
 */
export const explorerLayoutPath = (isBranch: boolean) =>
  (isBranch ? BRANCH_EXPLORER_PATHS : SERVICE_EXPLORER_PATHS).layout;

/**
 * The match for one of the area's targets. The area picks the route to match,
 * rather than matching both and choosing after: a ternary argument is not a
 * conditional hook call, and both route ids are in the tree.
 */
export const useExplorerMatch = <TTarget extends ExplorerTarget>(
  target: TTarget,
) => {
  const isBranch = useIsBranchExplorer();
  return useMatch({
    from: (isBranch ? BRANCH_MATCHES : SERVICE_MATCHES)[target],
    shouldThrow: false,
  });
};

/**
 * The Explorer's own navigation, so a click stays in the area it starts in.
 *
 * The functions are memoized because callers put them in effect dependencies.
 */
export const useExplorerNavigation = () => {
  const navigate = useNavigate();
  const area = useExplorerArea();

  const goHome = useCallback(
    (tab?: ExplorerHomeTab) =>
      navigate({
        ...explorerTarget(area, "home", {}),
        search: tab ? { tab } : {},
      }),
    [navigate, area],
  );

  const goObject = useCallback(
    ({
      schema,
      objectType,
      objectName,
      tab,
    }: {
      schema: string;
      objectType: string;
      objectName: string;
      tab?: ExplorerObjectTab;
    }) =>
      navigate({
        ...explorerTarget(area, "object", { schema, objectType, objectName }),
        search: tab ? { tab } : {},
      }),
    [navigate, area],
  );

  const goQuery = useCallback(
    (queryId: string) => navigate(explorerTarget(area, "query", { queryId })),
    [navigate, area],
  );

  return { goHome, goObject, goQuery };
};
